window.FoxTheme = window.FoxTheme || {};

FoxTheme.StjSocialVideo = class StjSocialVideo extends HTMLElement {
  static SMOOTHING_MS = 200;

  connectedCallback() {
    this.media = this.querySelector("[data-social-video-media]");
    if (!this.media) return;

    this.background = this.querySelector(".social-video__background");
    this.callout = this.querySelector("[data-social-video-callout]");
    this.expandButton = this.querySelector("[data-social-video-expand]");
    this.inlineVideo = this.querySelector("[data-social-video-inline] video-element");
    this.modal = this.expandButton && document.getElementById(this.expandButton.getAttribute("aria-controls"));
    this.lightboxVideo = this.modal?.querySelector("[data-social-video-lightbox-video] video-element");
    this.isInView = false;
    this.stoppers = [];
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.expandButton?.addEventListener("click", () => this.handleExpand(), { signal });
    this.callout?.addEventListener("click", () => this.handleCallout(), { signal });
    this.modal?.addEventListener("modal:handleAfterHide", () => this.handleLightboxHide(), { signal });

    this.setupScrollMotion();
    if (this.inlineVideo) this.setupPlayback();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.stoppers?.forEach((stop) => stop && stop());
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }

  createAnimation(el, keyframes, options) {
    if (!el) return null;
    const animation = FoxTheme.Motion.animate(el, keyframes, { duration: 1, ...options });
    animation.pause();
    return animation;
  }

  setupScrollMotion() {
    if (FoxTheme.config?.motionReduced || !FoxTheme.Motion) return;

    const tilted =
      "perspective(105rem) translate3d(-3.2rem, 3.2rem, 0) rotateX(24deg) rotateY(28deg) rotateZ(-3.5deg) scale(0.84)";
    const flat = "perspective(120rem) translate3d(0, 0, 0) rotateX(0deg) rotateY(0deg) rotateZ(0deg) scale(1)";

    this.animations = [
      this.createAnimation(this.media, { transform: [tilted, flat] }, { easing: "linear" }),
      this.createAnimation(
        this.background,
        {
          opacity: [0, 0, 1],
          transform: ["translateY(2.8rem) scale(0.92)", "translateY(2.8rem) scale(0.92)", "translateY(0) scale(1)"],
        },
        { offset: [0, 0.5, 0.82], easing: ["linear", "ease-out"] },
      ),
      this.createAnimation(
        this.callout,
        { opacity: [0, 0, 1], transform: ["translateY(1.8rem)", "translateY(1.8rem)", "translateY(0)"] },
        { offset: [0, 0.76, 1], easing: ["linear", "ease-out"] },
      ),
    ].filter(Boolean);

    this.progress = 0;
    this.targetProgress = 0;
    this.hasSnapped = false;
    this.rafId = null;

    this.stoppers.push(
      FoxTheme.Motion.scroll((info) => this.handleScrollProgress(info), {
        target: this,
        offset: ["start end", "center center"],
      }),
    );
  }

  handleScrollProgress(info) {
    this.targetProgress = info.y.progress;

    if (!this.hasSnapped) {
      this.hasSnapped = true;
      this.progress = this.targetProgress;
      this.applyProgress();
      return;
    }

    this.startProgressLoop();
  }

  applyProgress() {
    this.animations.forEach((animation) => {
      animation.currentTime = this.progress;
    });
  }

  startProgressLoop() {
    if (this.rafId) return;

    let lastTime = performance.now();

    const tick = (now) => {
      const dt = Math.max(0, Math.min(now - lastTime, 64));
      lastTime = now;

      const delta = this.targetProgress - this.progress;
      if (Math.abs(delta) < 0.001) {
        this.progress = this.targetProgress;
        this.applyProgress();
        this.rafId = null;
        return;
      }

      this.progress += delta * (1 - Math.exp(-dt / StjSocialVideo.SMOOTHING_MS));
      this.applyProgress();
      this.rafId = requestAnimationFrame(tick);
    };

    this.rafId = requestAnimationFrame(tick);
  }

  setupPlayback() {
    if (!FoxTheme.Motion) return;

    this.stoppers.push(
      FoxTheme.Motion.inView(
        this.media,
        () => {
          this.isInView = true;
          if (!this.inlineVideo.paused) this.inlineVideo.play();
          return () => {
            this.isInView = false;
            if (!this.modal?.open) this.inlineVideo.pause();
          };
        },
        { amount: 0.6 },
      ),
    );
  }

  handleExpand() {
    if (!this.lightboxVideo) return;
    this.inlineVideo.pause();
    this.lightboxVideo.play();

    const from = this.inlineVideo.querySelector("video");
    const to = this.lightboxVideo.querySelector("video");
    if (from && to) to.currentTime = from.currentTime;
  }

  handleLightboxHide() {
    this.lightboxVideo?.pause();
    if (this.isInView && !this.inlineVideo.paused) this.inlineVideo.play();
  }

  handleCallout() {
    const wantedTitle = (this.callout.dataset.tabTitle || "").trim().toLowerCase();
    if (!wantedTitle) return;

    const trigger = Array.from(document.querySelectorAll("[data-custom-tabs] [data-tab-trigger]")).find(
      (el) => el.textContent.trim().toLowerCase() === wantedTitle,
    );
    if (!trigger) return;

    trigger.click();
    trigger.closest("[data-custom-tabs]")?.scrollIntoView({
      behavior: FoxTheme.config?.motionReduced ? "auto" : "smooth",
      block: "start",
    });
  }
};

if (!customElements.get("stj-social-video")) {
  customElements.define("stj-social-video", FoxTheme.StjSocialVideo);
}
